import { useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/context/AuthContext';
import { formatSize, fileIconFor, matchesSearch } from '@/lib/cloud';
import type { CloudFile } from '@/lib/cloud';

const BUCKET = 'media';

type View = 'files' | 'trash';

function FileManager() {
  const { user } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<CloudFile[]>([]);
  const [folder, setFolder] = useState('');
  const [search, setSearch] = useState('');
  const [view, setView] = useState<View>('files');
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const loadFiles = async () => {
    if (!user) return;
    setError(null);
    try {
      const { data, error: listError } = await supabase
        .from('cloud_files')
        .select('*')
        .eq('owner_id', user.id)
        .order('created_at', { ascending: false });
      if (listError) throw listError;
      setFiles((data as CloudFile[]) || []);
    } catch {
      setError('Impossible de charger vos fichiers.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      loadFiles();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const activeFiles = useMemo(
    () => files.filter((f) => !f.deleted_at),
    [files]
  );
  const trashedFiles = useMemo(
    () => files.filter((f) => f.deleted_at),
    [files]
  );

  const visibleFiles = useMemo(() => {
    const base = view === 'trash' ? trashedFiles : activeFiles;
    return base.filter((f) => matchesSearch(f, search));
  }, [activeFiles, trashedFiles, view, search]);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const list = e.target.files;
    if (!user || !list || list.length === 0) return;
    setError(null);
    setSuccess(null);
    setUploading(true);

    const safeFolder = folder.trim().replace(/\/+/g, '').replace(/^\/|\/$/g, '') || 'root';
    try {
      for (const file of Array.from(list)) {
        const ext = file.name.split('.').pop()?.toLowerCase() || 'bin';
        const safeName = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
        const path = `cloud/${user.id}/${safeFolder}/${safeName}`;

        const { error: upError } = await supabase.storage
          .from(BUCKET)
          .upload(path, file, { cacheControl: '3600', upsert: false });
        if (upError) throw upError;

        const { data: pub } = supabase.storage.from(BUCKET).getPublicUrl(path);

        const { error: dbError } = await supabase.from('cloud_files').insert({
          owner_id: user.id,
          name: file.name,
          size: file.size,
          type: file.type || null,
          url: pub.publicUrl,
          folder: safeFolder,
        });
        if (dbError) throw dbError;
      }
      setSuccess(`${list.length} fichier(s) téléversé(s) avec succès !`);
      setFolder('');
      setView('files');
      await loadFiles();
    } catch {
      setError('Une erreur est survenue lors du téléversement.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleTrash = async (f: CloudFile) => {
    setError(null);
    setSuccess(null);
    try {
      const { error: dbError } = await supabase
        .from('cloud_files')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', f.id);
      if (dbError) throw dbError;
      setFiles((prev) =>
        prev.map((x) => (x.id === f.id ? { ...x, deleted_at: new Date().toISOString() } : x))
      );
      setSuccess('Fichier déplacé vers la corbeille.');
    } catch {
      setError('Impossible de déplacer le fichier vers la corbeille.');
    }
  };

  const handleRestore = async (f: CloudFile) => {
    setError(null);
    setSuccess(null);
    try {
      const { error: dbError } = await supabase
        .from('cloud_files')
        .update({ deleted_at: null })
        .eq('id', f.id);
      if (dbError) throw dbError;
      setFiles((prev) => prev.map((x) => (x.id === f.id ? { ...x, deleted_at: null } : x)));
      setSuccess('Fichier restauré.');
    } catch {
      setError('Impossible de restaurer le fichier.');
    }
  };

  const handlePermanentDelete = async (f: CloudFile) => {
    setError(null);
    setSuccess(null);
    try {
      const { error: dbError } = await supabase.from('cloud_files').delete().eq('id', f.id);
      if (dbError) throw dbError;
      setFiles((prev) => prev.filter((x) => x.id !== f.id));
      setSuccess('Fichier supprimé définitivement.');
    } catch {
      setError('Impossible de supprimer définitivement le fichier.');
    }
  };

  const handleCopy = async (f: CloudFile) => {
    try {
      await navigator.clipboard.writeText(f.url);
      setCopiedId(f.id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      setError('Impossible de copier le lien.');
    }
  };

  const totalSize = activeFiles.reduce((sum, f) => sum + (f.size || 0), 0);

  return (
    <div>
      <div className="flex items-center justify-between gap-4 flex-wrap mb-5">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 flex items-center justify-center rounded-lg bg-primary-500/10">
            <i className="ri-folder-cloud-line text-xl text-primary-400" />
          </div>
          <div>
            <h3 className="font-heading font-semibold text-lg text-foreground-50">
              Mes fichiers
            </h3>
            <p className="text-xs text-foreground-400">
              {activeFiles.length} fichier(s) · {formatSize(totalSize)} utilisés
            </p>
          </div>
        </div>
        {trashedFiles.length > 0 && (
          <button
            type="button"
            onClick={() => setView(view === 'trash' ? 'files' : 'trash')}
            className="px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 bg-secondary-100 text-secondary-900 hover:bg-secondary-200 transition-colors whitespace-nowrap"
          >
            <i className="ri-delete-bin-6-line" />
            {view === 'trash' ? 'Retour aux fichiers' : `Corbeille (${trashedFiles.length})`}
          </button>
        )}
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-lg bg-accent-100/70 text-accent-900 text-sm">
          {error}
        </div>
      )}
      {success && (
        <div className="mb-4 p-3 rounded-lg bg-primary-100 text-primary-900 text-sm">
          {success}
        </div>
      )}

      {view === 'files' && (
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mb-5">
          <input
            type="text"
            value={folder}
            onChange={(e) => setFolder(e.target.value)}
            placeholder="Dossier (ex : documents) — vide = racine"
            className="flex-1 min-w-0 px-4 py-3 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-50 placeholder-foreground-600/50 focus:outline-none focus:border-primary-400"
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="px-5 py-3 bg-primary-500 text-foreground-950 font-semibold rounded-lg hover:bg-primary-400 transition-colors disabled:opacity-60 whitespace-nowrap text-sm"
          >
            {uploading ? 'Téléversement…' : 'Téléverser'}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={handleUpload}
            className="hidden"
          />
        </div>
      )}

      <div className="relative mb-5">
        <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-500 text-base" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={view === 'trash' ? 'Rechercher dans la corbeille…' : 'Rechercher par nom, extension ou dossier…'}
          className="w-full pl-10 pr-4 py-3 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-50 placeholder-foreground-600/50 focus:outline-none focus:border-primary-400"
        />
      </div>

      {loading ? (
        <div className="flex items-center justify-center min-h-[160px]">
          <i className="ri-loader-4-line animate-spin text-2xl text-primary-400" />
        </div>
      ) : visibleFiles.length === 0 ? (
        <div className="p-8 text-center rounded-xl border border-dashed border-background-300/60">
          <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-3">
            <i
              className={`${
                view === 'trash' ? 'ri-delete-bin-6-line' : 'ri-upload-cloud-line'
              } text-2xl text-foreground-400`}
            />
          </div>
          <p className="font-heading font-semibold text-foreground-50 mb-1">
            {view === 'trash' ? 'La corbeille est vide' : search ? 'Aucun résultat' : 'Aucun fichier pour le moment'}
          </p>
          <p className="text-sm text-foreground-400">
            {view === 'trash'
              ? 'Les fichiers supprimés apparaîtront ici pendant 30 jours.'
              : search
                ? 'Essayez un autre mot-clé.'
                : 'Téléversez vos premiers fichiers pour les stocker dans le Cloud.'}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {visibleFiles.map((f) => (
            <div
              key={f.id}
              className="flex items-center gap-3 p-3 rounded-lg bg-background-100 border border-background-200/70 hover:border-primary-400/40 transition-all"
            >
              <div className="w-10 h-10 shrink-0 flex items-center justify-center rounded-lg bg-background-50">
                <i className={`${fileIconFor(f.name, f.type)} text-lg text-primary-400`} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground-50 truncate">{f.name}</p>
                <p className="text-xs text-foreground-400">
                  {formatSize(f.size)} · {f.folder}
                </p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {view === 'trash' ? (
                  <>
                    <button
                      type="button"
                      onClick={() => handleRestore(f)}
                      title="Restaurer"
                      className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-500 hover:text-green-500 hover:bg-background-200/60 transition-colors"
                    >
                      <i className="ri-arrow-go-back-line" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handlePermanentDelete(f)}
                      title="Supprimer définitivement"
                      className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-500 hover:text-accent-500 hover:bg-background-200/60 transition-colors"
                    >
                      <i className="ri-delete-bin-6-line" />
                    </button>
                  </>
                ) : (
                  <>
                    <a
                      href={f.url}
                      target="_blank"
                      rel="noreferrer noopener"
                      title="Ouvrir"
                      className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-500 hover:text-foreground-50 hover:bg-background-200/60 transition-colors"
                    >
                      <i className="ri-external-link-line" />
                    </a>
                    <button
                      type="button"
                      onClick={() => handleCopy(f)}
                      title="Copier le lien"
                      className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-500 hover:text-foreground-50 hover:bg-background-200/60 transition-colors"
                    >
                      <i className={copiedId === f.id ? 'ri-check-line text-green-500' : 'ri-link'} />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleTrash(f)}
                      title="Déplacer vers la corbeille"
                      className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-500 hover:text-accent-500 hover:bg-background-200/60 transition-colors"
                    >
                      <i className="ri-delete-bin-6-line" />
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default FileManager;